import {FC, useMemo} from "react";
import {Typography} from "antd";

const {Text} = Typography;

// Categorical palette tuned for the dark background. We cycle through
// the list when there are more parieurs than colors; the current user's
// line is always drawn slightly thicker on top so it remains readable.
const palette = [
    '#66bb6a', '#42a5f5', '#ef5350', '#ffa726', '#ab47bc',
    '#26c6da', '#ec407a', '#fdd835', '#8d6e63', '#7e57c2',
    '#26a69a', '#d4e157', '#5c6bc0', '#ff7043', '#bdbdbd',
];

export type ProgressSeries = {
    usr_idt: number;
    parieur: string;
    points: number[];
    isMe?: boolean;
};

type Props = {
    journeeLabels: string[];
    series: ProgressSeries[];
    height?: number;
};

const ParieursProgressChart: FC<Props> = ({journeeLabels, series, height = 320}) => {

    const maxPoints = useMemo(() => {
        let m = 0;
        for (const s of series) {
            for (const p of s.points) if (p > m) m = p;
        }
        return m;
    }, [series]);

    const niceMax = useMemo(() => {
        if (maxPoints <= 0) return 1;
        const exp = Math.pow(10, Math.floor(Math.log10(maxPoints)));
        return Math.max(1, Math.ceil(maxPoints / exp) * exp);
    }, [maxPoints]);

    if (journeeLabels.length === 0 || series.length === 0) return null;

    const padding = {top: 12, right: 12, bottom: 26, left: 32};
    const svgWidth = 360;
    const innerWidth = svgWidth - padding.left - padding.right;
    const innerHeight = height - padding.top - padding.bottom - 36; // header room

    const xCoord = (i: number) => {
        if (journeeLabels.length <= 1) return padding.left + innerWidth / 2;
        return padding.left + (i / (journeeLabels.length - 1)) * innerWidth;
    };
    const yCoord = (v: number) => padding.top + innerHeight - (v / niceMax) * innerHeight;

    const yTicks = 4;
    const yTickValues = Array.from({length: yTicks + 1}, (_, i) => Math.round((niceMax * i) / yTicks));

    const seriesWithColor = series.map((s, i) => ({...s, color: palette[i % palette.length]}));

    return (
        <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            padding: 12,
            height,
            display: 'flex',
            flexDirection: 'column',
            color: '#fff',
            boxSizing: 'border-box',
        }}>
            <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8}}>
                <Text strong style={{fontSize: 13, color: '#fff'}}>Progression — par journée</Text>
            </div>
            <div style={{display: 'flex', flex: 1, minHeight: 0, gap: 8}}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    overflowY: 'auto',
                    paddingRight: 4,
                    width: 96,
                    flexShrink: 0,
                }}>
                    {seriesWithColor.map((s) => (
                        <div key={s.usr_idt} style={{display: 'flex', alignItems: 'center', gap: 4, fontSize: 11}}>
                            <span style={{
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                background: s.color,
                                flexShrink: 0,
                            }}/>
                            <span
                                title={s.parieur}
                                style={{
                                    color: s.isMe ? '#42a5f5' : '#fff',
                                    fontWeight: s.isMe ? 600 : 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {s.parieur}
                            </span>
                        </div>
                    ))}
                </div>
                <svg
                    viewBox={`0 0 ${svgWidth} ${height - 30}`}
                    preserveAspectRatio="none"
                    style={{flex: 1, minWidth: 0, height: '100%'}}
                >
                    {yTickValues.map((v, i) => (
                        <g key={`y-${i}`}>
                            <line
                                x1={padding.left}
                                x2={svgWidth - padding.right}
                                y1={yCoord(v)}
                                y2={yCoord(v)}
                                stroke="rgba(255,255,255,0.10)"
                                strokeDasharray="2 3"
                            />
                            <text
                                x={padding.left - 4}
                                y={yCoord(v) + 3}
                                textAnchor="end"
                                fill="rgba(255,255,255,0.55)"
                                fontSize="9"
                            >
                                {v}
                            </text>
                        </g>
                    ))}
                    {journeeLabels.map((lib, i) => (
                        <text
                            key={`x-${i}`}
                            x={xCoord(i)}
                            y={padding.top + innerHeight + 14}
                            textAnchor="middle"
                            fill="rgba(255,255,255,0.55)"
                            fontSize="9"
                        >
                            {lib}
                        </text>
                    ))}
                    {seriesWithColor.map((s) => {
                        const pointsAttr = s.points.map((v, j) => `${xCoord(j)},${yCoord(v)}`).join(' ');
                        return (
                            <g key={s.usr_idt}>
                                <polyline
                                    points={pointsAttr}
                                    stroke={s.color}
                                    strokeWidth={s.isMe ? 2.4 : 1.4}
                                    fill="none"
                                    opacity={s.isMe ? 1 : 0.85}
                                />
                                {s.points.map((v, j) => (
                                    <circle
                                        key={j}
                                        cx={xCoord(j)}
                                        cy={yCoord(v)}
                                        r={s.isMe ? 3 : 2}
                                        fill={s.color}
                                    />
                                ))}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

export default ParieursProgressChart;
